"use client";

import React, { useState } from "react";
import { ArrowRight, ArrowDown, Cpu, Layers, Trophy, CircleCheck as CheckCircle2, Sparkles, ChevronRight, Compass, GraduationCap, Zap, Waypoints } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
interface CourseNode {
  code: string;
  titleVi: string;
  titleEn: string;
  age: string;
  platform: string;
  path: string;
  descVi: string;
  descEn: string;
  tagVi: string;
  tagEn: string;
}
const DUPLO_COURSES: CourseNode[] = [{
  code: "D1",
  titleVi: "Bé làm kỹ sư",
  titleEn: "Little Engineer",
  age: "4–5",
  platform: "Duplo",
  path: "/RoboticsCourseD1Page",
  descVi: "Làm quen với chi tiết lắp ráp, đọc hình theo từng bước, kết cấu vững & tư duy không máy tính.",
  descEn: "Introduction to physical building, step-by-step model reading, stable structures & unplugged logic.",
  tagVi: "Lắp ráp & Thứ tự",
  tagEn: "Assembly & Order"
}, {
  code: "D2",
  titleVi: "Thành phố của em",
  titleEn: "My City",
  age: "4–5",
  platform: "Duplo",
  path: "/RoboticsCourseD2Page",
  descVi: "Bánh răng, đòn bẩy, cơ cấu nâng hạ và chuỗi lệnh logic 4–6 bước theo chủ đề đô thị.",
  descEn: "Gears, levers, lifting mechanisms and 4–6 step logical command sequences in city themes.",
  tagVi: "Cơ cấu & Nếu... thì",
  tagEn: "Mechanisms & Logic"
}, {
  code: "D3",
  titleVi: "Cỗ máy kỳ diệu",
  titleEn: "Amazing Machines",
  age: "5–6",
  platform: "Duplo",
  path: "/RoboticsCourseD3Page",
  descVi: "Thanh truyền, tay quay, tay gắp, bánh xích kết hợp lưu đồ thuật toán hình ảnh và vòng lặp.",
  descEn: "Linkages, cranks, grippers, tracks paired with visual flowcharts and conditional loops.",
  tagVi: "Lưu đồ & Vòng lặp",
  tagEn: "Flowcharts & Loops"
}, {
  code: "D4",
  titleVi: "Động cơ nhí",
  titleEn: "Junior Motors",
  age: "5–6",
  platform: "Duplo",
  path: "/RoboticsCourseD4Page",
  descVi: "Pin, công tắc, động cơ điện, thẻ lệnh định thời và nguyên lý Input → Process → Output.",
  descEn: "Battery, switch, electrical motors, timed command cards, and Input → Process → Output concepts.",
  tagVi: "Cầu nối sang lập trình",
  tagEn: "Bridge to Coding"
}];
const WHALESBOT_COURSES: CourseNode[] = [{
  code: "HP1",
  titleVi: "Kỹ sư cơ khí nhí",
  titleEn: "Junior Mechanical Engineer",
  age: "7+",
  platform: "WhalesBot AI Module 1S",
  path: "/RoboticsCourseHP1Page",
  descVi: "Bộ điều khiển, động cơ, Block Studio, lập trình trình tự, tốc độ, góc quay và cơ cấu truyền động.",
  descEn: "Controller, motors, Block Studio, sequential programming, speed, angle control, and gear trains.",
  tagVi: "Lập trình robot thực thụ",
  tagEn: "true Robot Coding"
}, {
  code: "HP2",
  titleVi: "Robot biết cảm nhận",
  titleEn: "Sensing Robots",
  age: "7+",
  platform: "WhalesBot AI Module 1S",
  path: "/RoboticsCourseHP2Page",
  descVi: "Cảm biến hồng ngoại, cảm biến chạm, điều kiện if/else, biến số và bánh răng côn, trục vít.",
  descEn: "Infrared and touch sensors, conditionals, variables, bevel gears, chain, and worm gears.",
  tagVi: "Cảm biến & Phản hồi",
  tagEn: "Sensors & Responses"
}, {
  code: "HP3",
  titleVi: "Xe robot đầu tiên",
  titleEn: "First Robot Car",
  age: "7+",
  platform: "WhalesBot AI Module 1S",
  path: "/RoboticsCourseHP3Page",
  descVi: "Khung gầm chuẩn, chuyển động chính xác góc quay 90°/180°/360°, tính chu vi bánh và My Blocks.",
  descEn: "Standard mobile chassis, precision movement, 90°/180°/360° turns, wheel calculations & My Blocks.",
  tagVi: "Khung gầm & Di động",
  tagEn: "Chassis & Mobility"
}, {
  code: "HP4",
  titleVi: "Xe robot thông minh",
  titleEn: "Smart Mobile Robot",
  age: "7+",
  platform: "WhalesBot AI Module 1S",
  path: "/RoboticsCourseHP4Page",
  descVi: "Tránh vật cản đa điểm, chống rơi bậc, đếm vạch, cơ cấu đẩy thụ động và thuật toán ngẫu nhiên.",
  descEn: "Obstacle avoidance, edge-drop safety, line counting, passive pushing mechanisms & timers.",
  tagVi: "Phản ứng môi trường",
  tagEn: "Environment Reactive"
}, {
  code: "HP5",
  titleVi: "Thành phố xe tự lái",
  titleEn: "Autonomous City Car",
  age: "7+",
  platform: "WhalesBot AI Module 1S",
  path: "/RoboticsCourseHp5Page",
  descVi: "Cảm biến quang sai 5 kênh (grayscale), bám line, nhận diện ngã tư, hiệu chỉnh P và tìm đường ngắn nhất.",
  descEn: "5-channel grayscale sensor, line following, intersection recognition, P-correction and route mapping.",
  tagVi: "Tự hành & Lập bản đồ",
  tagEn: "Autonomous & Mapping"
}, {
  code: "HP6",
  titleVi: "Robot nhiệm vụ",
  titleEn: "Mission Robot",
  age: "7+",
  platform: "WhalesBot AI Module 1S",
  path: "/RoboticsCourseHP6Page",
  descVi: "Cơ cấu chấp hành phức hợp (nâng/thả/bắn), biến trạng thái, chiến thuật 150 giây và mô phỏng đấu trường.",
  descEn: "Mission multi-mechanisms, state variables, 150s mission strategy, and simulated tournament arena.",
  tagVi: "Chiến thuật & Nhiệm vụ",
  tagEn: "Strategy & Mission"
}];
const ALL_10_LEVELS = [...DUPLO_COURSES.map((c, i) => ({
  ...c,
  level: i + 1,
  stage: "Duplo"
})), ...WHALESBOT_COURSES.map((c, i) => ({
  ...c,
  level: i + 5,
  stage: "WhalesBot AI"
}))];
export default function PathwayOverviewRoadmap() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  return <section id="pathwayoverviewroadmap" data-nav="light" className="relative overflow-hidden bg-background text-foreground py-16 md:py-24">
      {/* Decorative technical grid background */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(var(--border)_1px,transparent_1px)] [background-size:24px_24px] opacity-40" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Controls & Language Switcher */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-border pb-6 mb-12">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold text-primary">
              <Compass className="size-3.5" />
              <span>ROBOTO CURRICULUM ARCHITECTURE</span>
            </div>
            <h2 className="mt-3 text-2xl md:text-4xl font-bold tracking-tight text-foreground font-serif">
              {lang === "vi" ? "TỔNG QUAN LỘ TRÌNH ROBOTICS" : "ROBOTICS PATHWAY OVERVIEW"}
            </h2>
            <p className="mt-2 text-sm md:text-base text-muted-foreground max-w-2xl">
              {lang === "vi" ? "Lộ trình đào tạo dài hạn tiêu chuẩn cao: Từ cơ khí lắp ráp Duplo, qua lập trình WhalesBot AI, đến thử thách nhiệm vụ thi đấu độc lập." : "A rigorous long-term STEM curriculum: From Duplo hands-on engineering, through WhalesBot AI programming, to independent competition missions."}
            </p>
          </div>

          <div className="flex items-center gap-2 self-start sm:self-center">
            <span className="text-xs font-medium text-muted-foreground mr-1">
              {lang === "vi" ? "Ngôn ngữ:" : "Language:"}
            </span>
            <div className="inline-flex rounded-lg border border-border bg-muted/60 p-1">
              <Button variant={lang === "vi" ? "default" : "ghost"} size="sm" className="h-7 px-3 text-xs" onClick={() => setLang("vi")}>
                Tiếng Việt
              </Button>
              <Button variant={lang === "en" ? "default" : "ghost"} size="sm" className="h-7 px-3 text-xs" onClick={() => setLang("en")}>
                English
              </Button>
            </div>
          </div>
        </div>

        {/* 10-LEVEL VISUAL ROADMAP GRAPHIC / LADDER DIAGRAM */}
        <div className="mb-16 rounded-3xl border border-slate-800 bg-slate-950 p-6 md:p-10 shadow-2xl relative overflow-hidden text-slate-100">
          {/* Subtle laboratory grid background */}
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] opacity-30" aria-hidden="true" />

          {/* Blueprint Title Header */}
          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-800 pb-6 mb-8">
            <div>
              <div className="inline-flex items-center gap-2 rounded-md bg-amber-500/10 border border-amber-500/30 px-2.5 py-1 text-xs font-mono font-semibold text-amber-400 mb-2">
                <Waypoints className="size-3.5" />
                <span>10-LEVEL CONTINUOUS PROGRESSION MATRIX</span>
              </div>
              <h3 className="text-xl md:text-3xl font-bold tracking-tight text-white font-serif">
                {lang === "vi" ? "Sơ đồ Thang Năng Lực 10 Cấp Độ Continuous Pathway" : "10-Level Technical Competency Ladder"}
              </h3>
            </div>
            <div className="flex items-center gap-4 text-xs font-mono">
              <div className="flex items-center gap-2 bg-slate-900 border border-slate-800 px-3 py-1.5 rounded-lg">
                <span className="size-2.5 rounded-full bg-amber-500 animate-pulse" />
                <span className="text-amber-400 font-bold">LVL 01–04: DUPLO</span>
              </div>
              <div className="flex items-center gap-2 bg-slate-900 border border-slate-800 px-3 py-1.5 rounded-lg">
                <span className="size-2.5 rounded-full bg-cyan-400 animate-pulse" />
                <span className="text-cyan-300 font-bold">LVL 05–10: WHALESBOT AI</span>
              </div>
            </div>
          </div>

          {/* Connected Path Diagram (Desktop Ladder Horizontal Grid) */}
          <div className="relative z-10 hidden xl:block">
            {/* Continuous SVG Connection Line */}
            <div className="absolute top-1/2 left-[4%] right-[4%] -translate-y-1/2 h-1 bg-slate-800 rounded-full overflow-hidden pointer-events-none">
              <div className="h-full bg-gradient-to-r from-amber-500 via-amber-400 via-cyan-400 to-rose-500 w-full" />
            </div>

            <div className="grid grid-cols-10 gap-2 relative">
              {ALL_10_LEVELS.map((item, i) => {
              const isDuplo = item.level <= 4;
              return <div key={item.code} data-index={i} className="group relative flex flex-col items-center text-center">
                    {/* Level Pill */}
                    <div className="text-[10px] font-mono font-bold text-slate-400 mb-2 uppercase tracking-wider">
                      LVL {item.level < 10 ? `0${item.level}` : item.level}
                    </div>

                    {/* Node Connector Ring */}
                    <Link to={item.path} className={`relative z-10 size-12 rounded-2xl flex items-center justify-center font-mono font-extrabold text-sm transition-all duration-300 shadow-lg ${isDuplo ? "bg-slate-900 border-2 border-amber-500 text-amber-400 hover:bg-amber-500 hover:text-slate-950 hover:scale-110 hover:shadow-amber-500/25" : "bg-slate-900 border-2 border-cyan-400 text-cyan-300 hover:bg-cyan-400 hover:text-slate-950 hover:scale-110 hover:shadow-cyan-400/25"}`}>
                      {item.code}
                    </Link>

                    {/* Card Content Node */}
                    <div className="mt-3 w-full bg-slate-900/90 border border-slate-800 hover:border-slate-700 rounded-xl p-2.5 transition-all duration-200 group-hover:scale-105 group-hover:bg-slate-900">
                      <div className="text-[10px] font-semibold text-slate-400 mb-1 truncate">
                        {item.age} {lang === "vi" ? "tuổi" : "yo"}
                      </div>
                      <div className="text-xs font-bold text-slate-100 line-clamp-1 group-hover:text-amber-300 transition-colors">
                        {lang === "vi" ? item.titleVi : item.titleEn}
                      </div>
                      <div className="mt-1.5 text-[9px] font-medium px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 truncate">
                        {lang === "vi" ? item.tagVi : item.tagEn}
                      </div>
                    </div>

                    {/* Transition Indicator between L4 & L5 */}
                    {item.level === 4 && <div className="absolute -right-3 top-2 z-20 size-6 rounded-full bg-slate-950 border-2 border-amber-400 text-amber-400 flex items-center justify-center shadow-md">
                        <Zap className="size-3 animate-pulse" />
                      </div>}
                  </div>;
            })}
            </div>
          </div>

          {/* Connected Path Diagram (Tablet & Mid-screen Grid 5x2) */}
          <div className="relative z-10 hidden md:block xl:hidden">
            <div className="grid grid-cols-5 gap-4">
              {ALL_10_LEVELS.map((item, i) => {
              const isDuplo = item.level <= 4;
              return <Link key={item.code} data-index={i} to={item.path} className={`group relative rounded-xl border p-4 bg-slate-900/80 transition-all hover:scale-102 ${isDuplo ? "border-amber-500/40 hover:border-amber-400" : "border-cyan-400/40 hover:border-cyan-300"}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-mono text-slate-400">LVL {item.level < 10 ? `0${item.level}` : item.level}</span>
                      <Badge variant="outline" className={`text-[10px] font-mono ${isDuplo ? "border-amber-500/50 text-amber-400" : "border-cyan-400/50 text-cyan-300"}`}>
                        {item.code}
                      </Badge>
                    </div>
                    <div className="text-xs font-bold text-white group-hover:text-amber-300 transition-colors">
                      {lang === "vi" ? item.titleVi : item.titleEn}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-1">{item.age} {lang === "vi" ? "tuổi" : "yo"} • {item.platform}</div>
                  </Link>;
            })}
            </div>
          </div>

          {/* Connected Ladder Diagram (Mobile Vertical Snake / Spine) */}
          <div className="relative z-10 block md:hidden">
            <div className="relative pl-6 before:absolute before:left-3 before:top-3 before:bottom-3 before:w-1 before:bg-gradient-to-b before:from-amber-500 before:via-cyan-400 before:to-rose-500 space-y-4">
              {ALL_10_LEVELS.map((item, i) => {
              const isDuplo = item.level <= 4;
              return <div key={item.code} data-index={i} className="relative">
                    {/* Node Dot */}
                    <div className={`absolute -left-[27px] top-3 size-4 rounded-full border-2 bg-slate-950 flex items-center justify-center ${isDuplo ? "border-amber-400" : "border-cyan-400"}`}>
                      <div className={`size-1.5 rounded-full ${isDuplo ? "bg-amber-400" : "bg-cyan-400"}`} />
                    </div>

                    <Link to={item.path} className="block rounded-xl border border-slate-800 bg-slate-900/90 p-3 shadow-sm hover:border-slate-700 transition-colors">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-bold text-slate-400">
                            LVL {item.level < 10 ? `0${item.level}` : item.level}
                          </span>
                          <span className={`font-mono text-xs font-bold px-1.5 py-0.5 rounded ${isDuplo ? "bg-amber-500/20 text-amber-300" : "bg-cyan-500/20 text-cyan-300"}`}>
                            {item.code}
                          </span>
                        </div>
                        <span className="text-[10px] text-slate-400">{item.age} {lang === "vi" ? "tuổi" : "yo"}</span>
                      </div>

                      <div className="text-xs font-bold text-slate-100">
                        {lang === "vi" ? item.titleVi : item.titleEn}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-1">
                        {lang === "vi" ? item.tagVi : item.tagEn}
                      </div>
                    </Link>

                    {item.level === 4 && <div className="my-3 py-2 px-3 rounded-lg border border-amber-500/40 bg-amber-500/10 text-[11px] font-mono text-amber-300 flex items-center gap-2">
                        <Zap className="size-3.5 shrink-0 text-amber-400 animate-pulse" />
                        <span>TRANSITION: DUPLO → WHALESBOT AI</span>
                      </div>}
                  </div>;
            })}
            </div>
          </div>
        </div>

        {/* 3-STAGE OVERVIEW PANELS */}
        <div className="mb-16">
          <div className="text-xs font-semibold tracking-wider uppercase text-muted-foreground mb-4">
            {lang === "vi" ? "CẤU TRÚC 3 GIAI ĐOẠN ĐÀO TẠO" : "3-STAGE CURRICULUM ARCHITECTURE"}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* STAGE 01 */}
            <Card className="relative overflow-hidden border-border/80 bg-card shadow-xs transition-shadow hover:shadow-md">
              <div className="absolute top-0 left-0 right-0 h-1 bg-chart-3" />
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="border-chart-3/50 text-chart-3 text-[11px] font-bold">
                    STAGE 01
                  </Badge>
                  <span className="text-xs font-bold text-muted-foreground">4–6 {lang === "vi" ? "TUỔI" : "Y.O"}</span>
                </div>

                <div className="mt-4">
                  <h3 className="text-lg font-bold text-foreground">
                    {lang === "vi" ? "ROBOTICS FOUNDATION" : "ROBOTICS FOUNDATION"}
                  </h3>
                  <p className="text-xs font-semibold text-primary mt-0.5">DUPLO ENGINEERING</p>
                  <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                    {lang === "vi" ? "Xây dựng tư duy kỹ thuật vật lý, cơ cấu máy móc và tư duy thuật toán trước khi chạm vào máy tính." : "Hands-on engineering thinking, physical mechanisms, and algorithmic thinking before digital screens."}
                  </p>
                </div>

                <Separator className="my-4" />

                <div className="flex items-center justify-between font-mono text-xs font-bold text-foreground bg-muted/40 p-2.5 rounded-lg border border-border/60">
                  <span className="text-primary">D1</span>
                  <ArrowRight className="size-3 text-muted-foreground" />
                  <span className="text-primary">D2</span>
                  <ArrowRight className="size-3 text-muted-foreground" />
                  <span className="text-primary">D3</span>
                  <ArrowRight className="size-3 text-muted-foreground" />
                  <span className="text-primary">D4</span>
                </div>
              </CardContent>
            </Card>

            {/* STAGE 02 */}
            <Card className="relative overflow-hidden border-border/80 bg-card shadow-xs transition-shadow hover:shadow-md ring-1 ring-primary/20">
              <div className="absolute top-0 left-0 right-0 h-1 bg-primary" />
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <Badge variant="default" className="text-[11px] font-bold">
                    STAGE 02
                  </Badge>
                  <span className="text-xs font-bold text-primary">7+ {lang === "vi" ? "TUỔI" : "Y.O"}</span>
                </div>

                <div className="mt-4">
                  <h3 className="text-lg font-bold text-foreground">
                    {lang === "vi" ? "ROBOTICS & PROGRAMMING" : "ROBOTICS & PROGRAMMING"}
                  </h3>
                  <p className="text-xs font-semibold text-primary mt-0.5">WHALESBOT AI MODULE 1S</p>
                  <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                    {lang === "vi" ? "Chuyển giao sang robot lập trình thực sự: điều khiển động cơ, cảm biến đa kênh, xe tự hành & giải quyết nhiệm vụ." : "Transition into programmable robotics: smart controllers, multi-sensors, autonomous vehicles, and missions."}
                  </p>
                </div>

                <Separator className="my-4" />

                <div className="flex items-center justify-between font-mono text-[11px] font-bold text-foreground bg-muted/40 p-2 rounded-lg border border-border/60 overflow-x-auto">
                  <span className="text-foreground">HP1</span>
                  <ArrowRight className="size-3 text-muted-foreground shrink-0" />
                  <span className="text-foreground">HP2</span>
                  <ArrowRight className="size-3 text-muted-foreground shrink-0" />
                  <span className="text-foreground">HP3</span>
                  <ArrowRight className="size-3 text-muted-foreground shrink-0" />
                  <span className="text-foreground">HP4</span>
                  <ArrowRight className="size-3 text-muted-foreground shrink-0" />
                  <span className="text-foreground">HP5</span>
                  <ArrowRight className="size-3 text-muted-foreground shrink-0" />
                  <span className="text-foreground">HP6</span>
                </div>
              </CardContent>
            </Card>

            {/* STAGE 03 */}
            <Card className="relative overflow-hidden border-border/80 bg-card shadow-xs transition-shadow hover:shadow-md">
              <div className="absolute top-0 left-0 right-0 h-1 bg-secondary" />
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="border-secondary/60 text-secondary text-[11px] font-bold">
                    STAGE 03
                  </Badge>
                  <span className="text-xs font-bold text-muted-foreground">
                    {lang === "vi" ? "SAU HP6" : "AFTER HP6"}
                  </span>
                </div>

                <div className="mt-4">
                  <h3 className="text-lg font-bold text-foreground">
                    {lang === "vi" ? "ADVANCED / COMPETITION" : "ADVANCED / COMPETITION"}
                  </h3>
                  <p className="text-xs font-semibold text-secondary mt-0.5">ENJOY AI PREPARATION</p>
                  <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                    {lang === "vi" ? "Lộ trình luyện thi tách biệt dành cho học viên hoàn thành HP6 sẵn sàng chinh phục đấu trường chuyên sâu (không phải HP7)." : "Dedicated competition preparation track for HP6 graduates pursuing arena tournaments (explicitly distinct from HP7)."}
                  </p>
                </div>

                <Separator className="my-4" />

                <div className="flex items-center justify-center gap-3 bg-secondary/10 text-secondary text-xs font-bold p-2.5 rounded-lg border border-secondary/20">
                  <Trophy className="size-4 shrink-0" />
                  <span>
                    {lang === "vi" ? "Khóa huấn luyện chuyên sâu ENJOY AI" : "Specialized ENJOY AI Track"}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* FULL VISUAL ROADMAP */}
        <div className="mt-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
            <div>
              <div className="text-xs font-semibold tracking-wider uppercase text-primary mb-1">
                {lang === "vi" ? "LỘ TRÌNH LIÊN TỤC 10 KHÓA HỌC" : "10-COURSE SEQUENTIAL ROADMAP"}
              </div>
              <h3 className="text-xl md:text-3xl font-bold text-foreground font-serif">
                {lang === "vi" ? "Bản đồ phát triển năng lực toàn diện" : "Comprehensive Student Roadmap"}
              </h3>
            </div>
            <p className="text-xs text-muted-foreground md:text-right max-w-sm">
              {lang === "vi" ? "Mỗi khóa 12 buổi × 90 phút. Tối đa 2–3 học sinh / bộ kit thực hành." : "12 sessions × 90 mins per course. 2–3 students per equipment kit."}
            </p>
          </div>

          {/* DESKTOP ROADMAP: Stepped / Horizontal Flow (Hidden on mobile) */}
          <div className="hidden lg:block space-y-8">
            {/* DUPLO SECTION BLOCK */}
            <div className="rounded-2xl border border-border/80 bg-card/60 p-6 shadow-xs backdrop-blur-sm">
              <div className="flex items-center justify-between pb-4 border-b border-border/60 mb-6">
                <div className="flex items-center gap-3">
                  <div className="size-8 rounded-lg bg-chart-3/20 text-chart-3 flex items-center justify-center font-bold text-sm">
                    01
                  </div>
                  <div>
                    <h4 className="font-bold text-base text-foreground">
                      {lang === "vi" ? "GIAI ĐOẠN 1: NỀN TẢNG DUPLO ENGINEERING (4–6 TUỔI)" : "PHASE 1: DUPLO ENGINEERING FOUNDATION (AGES 4–6)"}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {lang === "vi" ? "Khám phá cơ học, kết cấu vững chắc, đòn bẩy và tư duy thuật toán không máy tính" : "Discover physical mechanics, robust structures, levers, and unplugged algorithms"}
                    </p>
                  </div>
                </div>
                <Badge variant="outline" className="border-chart-3/30 text-chart-3 bg-chart-3/10">
                  4 {lang === "vi" ? "Khóa cơ sở" : "Core Courses"}
                </Badge>
              </div>

              {/* 4 Duplo Cards */}
              <div className="grid grid-cols-4 gap-4">
                {DUPLO_COURSES.map((course, idx) => <div key={course.code} data-index={idx} className="relative flex flex-col group">
                    <Card className="flex flex-col h-full border-border/80 bg-background transition-all hover:border-chart-3 hover:shadow-md">
                      <CardContent className="p-4 flex flex-col flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-mono text-xs font-bold px-2 py-0.5 rounded bg-chart-3/15 text-chart-3">
                            {course.code}
                          </span>
                          <span className="text-[11px] font-semibold text-muted-foreground">
                            {course.age} {lang === "vi" ? "tuổi" : "yo"}
                          </span>
                        </div>

                        <h5 className="font-bold text-sm text-foreground group-hover:text-primary transition-colors">
                          {lang === "vi" ? course.titleVi : course.titleEn}
                        </h5>
                        <p className="text-[11px] text-muted-foreground mt-1 line-clamp-3 leading-relaxed flex-1">
                          {lang === "vi" ? course.descVi : course.descEn}
                        </p>

                        <div className="mt-3 pt-3 border-t border-border/60">
                          <span className="inline-block text-[10px] font-medium text-chart-3 bg-chart-3/10 px-2 py-0.5 rounded-sm mb-3">
                            {lang === "vi" ? course.tagVi : course.tagEn}
                          </span>
                          <Button asChild size="sm" variant="outline" className="w-full text-xs h-7">
                            <Link to={course.path}>
                              {lang === "vi" ? `Chi tiết ${course.code}` : `View ${course.code}`}
                              <ChevronRight className="size-3 ml-1" />
                            </Link>
                          </Button>
                        </div>
                      </CardContent>
                    </Card>

                    {idx < DUPLO_COURSES.length - 1 && <div className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 size-6 rounded-full bg-card border border-border flex items-center justify-center shadow-xs text-muted-foreground">
                        <ArrowRight className="size-3" />
                      </div>}
                  </div>)}
              </div>
            </div>

            {/* VISUALLY PROMINENT BRIDGE MARKER */}
            <div className="relative my-6 flex items-center justify-center">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="w-full border-t-2 border-dashed border-primary/40" />
              </div>
              <div className="relative z-10 flex items-center gap-3 rounded-full border-2 border-primary bg-background px-6 py-2.5 shadow-md">
                <Sparkles className="size-4 text-primary animate-pulse" />
                <span className="font-mono text-xs font-bold tracking-wider uppercase text-primary">
                  {lang === "vi" ? "BƯỚC CHUYỂN TIẾP TRỌNG TÂM: DUPLO → WHALESBOT AI MODULE 1S" : "CORE PARADIGM SHIFT: DUPLO → WHALESBOT AI MODULE 1S"}
                </span>
                <span className="text-[11px] text-muted-foreground border-l border-border pl-3 hidden xl:inline">
                  {lang === "vi" ? "D4 hoàn thành hoặc kiểm tra đầu vào năng lực" : "Prerequisite: D4 or entrance skill assessment"}
                </span>
              </div>
            </div>

            {/* WHALESBOT SECTION BLOCK */}
            <div className="rounded-2xl border-2 border-primary/30 bg-card/60 p-6 shadow-xs backdrop-blur-sm">
              <div className="flex items-center justify-between pb-4 border-b border-border/60 mb-6">
                <div className="flex items-center gap-3">
                  <div className="size-8 rounded-lg bg-primary/20 text-primary flex items-center justify-center font-bold text-sm">
                    02
                  </div>
                  <div>
                    <h4 className="font-bold text-base text-foreground">
                      {lang === "vi" ? "GIAI ĐOẠN 2: ROBOTICS & PROGRAMMING (7+ TUỔI)" : "PHASE 2: ROBOTICS & PROGRAMMING (7+ Y.O)"}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {lang === "vi" ? "Hệ thiết bị WhalesBot AI Module 1S — Cơ khí thực tế, Cảm biến, Xe di động, Xe tự hành và Nhiệm vụ thi đấu" : "WhalesBot AI Module 1S system — Mechanics, Smart Sensors, Autonomous Mobile Robotics & Strategy"}
                    </p>
                  </div>
                </div>
                <Badge variant="default" className="text-xs">
                  6 {lang === "vi" ? "Khóa nâng cao" : "Advanced Courses"}
                </Badge>
              </div>

              {/* 6 WhalesBot Cards (Grid 6 or 2 rows of 3) */}
              <div className="grid grid-cols-6 gap-3">
                {WHALESBOT_COURSES.map((course, idx) => <div key={course.code} data-index={idx} className="relative flex flex-col group">
                    <Card className="flex flex-col h-full border-border/80 bg-background transition-all hover:border-primary hover:shadow-md">
                      <CardContent className="p-3.5 flex flex-col flex-1">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="font-mono text-xs font-bold px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                            {course.code}
                          </span>
                          <span className="text-[10px] font-semibold text-muted-foreground">7+ yo</span>
                        </div>

                        <h5 className="font-bold text-xs text-foreground group-hover:text-primary transition-colors line-clamp-1">
                          {lang === "vi" ? course.titleVi : course.titleEn}
                        </h5>
                        <p className="text-[10px] text-muted-foreground mt-1 line-clamp-3 leading-relaxed flex-1">
                          {lang === "vi" ? course.descVi : course.descEn}
                        </p>

                        <div className="mt-2.5 pt-2 border-t border-border/60">
                          <span className="inline-block text-[9px] font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded-sm mb-2 truncate max-w-full">
                            {lang === "vi" ? course.tagVi : course.tagEn}
                          </span>
                          <Button asChild size="sm" variant="outline" className="w-full text-[11px] h-6 px-1">
                            <Link to={course.path}>
                              {course.code}
                              <ChevronRight className="size-2.5 ml-0.5" />
                            </Link>
                          </Button>
                        </div>
                      </CardContent>
                    </Card>

                    {idx < WHALESBOT_COURSES.length - 1 && <div className="absolute -right-2 top-1/2 -translate-y-1/2 z-10 size-4 rounded-full bg-card border border-border flex items-center justify-center text-muted-foreground">
                        <ArrowRight className="size-2.5" />
                      </div>}
                  </div>)}
              </div>
            </div>

            {/* STAGE 3 - ENJOY AI TRANSITION BANNER */}
            <div className="rounded-2xl border border-secondary/40 bg-secondary/10 p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="size-12 rounded-xl bg-secondary text-secondary-foreground flex items-center justify-center shrink-0">
                  <Trophy className="size-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold px-2 py-0.5 rounded bg-secondary text-secondary-foreground">
                      STAGE 03
                    </span>
                    <h5 className="text-base font-bold text-foreground">
                      {lang === "vi" ? "KHÓA LUYỆN THI ĐỘC LẬP: ENJOY AI PREPARATION (SAU HP6)" : "INDEPENDENT TRACK: ENJOY AI PREPARATION (AFTER HP6)"}
                    </h5>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {lang === "vi" ? "Không phải khóa HP7. Lộ trình huấn luyện chiến thuật chuyên sâu dành cho học viên xuất sắc muốn tham gia giải đấu Robotics chính thức." : "Explicitly distinct from HP7. Dedicated strategic arena coaching for selected students targeting official robotics tournaments."}
                  </p>
                </div>
              </div>

              <Button asChild variant="secondary" className="shrink-0">
                <Link to="#enjoy-ai-pathway">
                  {lang === "vi" ? "Tìm hiểu Enjoy AI" : "Explore Enjoy AI"}
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>
            </div>
          </div>

          {/* MOBILE & TABLET ROADMAP: Dedicated Vertical Timeline */}
          <div className="block lg:hidden">
            <div className="relative pl-6 before:absolute before:left-3 before:top-2 before:bottom-2 before:w-0.5 before:bg-border space-y-6">
              {/* STAGE 1 BADGE */}
              <div className="relative">
                <div className="absolute -left-6 top-1 size-6 rounded-full bg-chart-3 text-white flex items-center justify-center text-[10px] font-bold">
                  01
                </div>
                <div className="font-bold text-sm text-chart-3 uppercase tracking-wider mb-2">
                  {lang === "vi" ? "Giai đoạn 1: Nền tảng Duplo (4–6 tuổi)" : "Stage 1: Duplo Foundation (Ages 4–6)"}
                </div>
              </div>

              {/* D1 - D4 Mobile Cards */}
              {DUPLO_COURSES.map((course, i) => <div key={course.code} data-index={i} className="relative">
                  <div className="absolute -left-[27px] top-4 size-3 rounded-full border-2 border-chart-3 bg-background" />
                  <Card className="border-border/80 bg-card hover:border-chart-3 shadow-xs">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono text-xs font-bold px-2 py-0.5 rounded bg-chart-3/15 text-chart-3">
                          {course.code}
                        </span>
                        <Badge variant="outline" className="text-[10px]">
                          {course.age} {lang === "vi" ? "tuổi" : "y.o"} • {course.platform}
                        </Badge>
                      </div>

                      <h5 className="font-bold text-base text-foreground">
                        {lang === "vi" ? course.titleVi : course.titleEn}
                      </h5>
                      <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                        {lang === "vi" ? course.descVi : course.descEn}
                      </p>

                      <div className="mt-3 flex items-center justify-between pt-2 border-t border-border">
                        <span className="text-[11px] font-medium text-chart-3">
                          {lang === "vi" ? course.tagVi : course.tagEn}
                        </span>
                        <Button asChild size="sm" variant="outline" className="h-8 text-xs">
                          <Link to={course.path}>
                            {lang === "vi" ? "Xem khóa học" : "View Course"}
                            <ChevronRight className="size-3.5 ml-1" />
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>)}

              {/* VISUALLY PROMINENT BRIDGE MARKER (MOBILE) */}
              <div className="relative py-4">
                <div className="absolute -left-[30px] top-1/2 -translate-y-1/2 size-4 rounded-full bg-primary text-white flex items-center justify-center">
                  <ArrowDown className="size-2.5" />
                </div>
                <div className="rounded-xl border-2 border-primary bg-primary/5 p-4 text-center">
                  <div className="inline-flex items-center gap-1.5 text-xs font-bold text-primary mb-1">
                    <Sparkles className="size-3.5" />
                    <span>DUPLO → WHALESBOT AI MODULE 1S</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    {lang === "vi" ? "Chuyển tiếp sang lập trình robot thực thụ sau D4 hoặc qua bài kiểm tra đầu vào." : "Transition to digital programmable robotics after D4 or placement assessment."}
                  </p>
                </div>
              </div>

              {/* STAGE 2 BADGE */}
              <div className="relative">
                <div className="absolute -left-6 top-1 size-6 rounded-full bg-primary text-white flex items-center justify-center text-[10px] font-bold">
                  02
                </div>
                <div className="font-bold text-sm text-primary uppercase tracking-wider mb-2">
                  {lang === "vi" ? "Giai đoạn 2: Lập trình WhalesBot (7+ tuổi)" : "Stage 2: WhalesBot AI (7+ Y.O)"}
                </div>
              </div>

              {/* HP1 - HP6 Mobile Cards */}
              {WHALESBOT_COURSES.map((course, i) => <div key={course.code} data-index={i} className="relative">
                  <div className="absolute -left-[27px] top-4 size-3 rounded-full border-2 border-primary bg-background" />
                  <Card className="border-border/80 bg-card hover:border-primary shadow-xs">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono text-xs font-bold px-2 py-0.5 rounded bg-primary/10 text-primary">
                          {course.code}
                        </span>
                        <Badge variant="default" className="text-[10px]">
                          7+ {lang === "vi" ? "tuổi" : "y.o"} • WhalesBot
                        </Badge>
                      </div>

                      <h5 className="font-bold text-base text-foreground">
                        {lang === "vi" ? course.titleVi : course.titleEn}
                      </h5>
                      <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                        {lang === "vi" ? course.descVi : course.descEn}
                      </p>

                      <div className="mt-3 flex items-center justify-between pt-2 border-t border-border">
                        <span className="text-[11px] font-medium text-primary">
                          {lang === "vi" ? course.tagVi : course.tagEn}
                        </span>
                        <Button asChild size="sm" variant="default" className="h-8 text-xs">
                          <Link to={course.path}>
                            {lang === "vi" ? "Xem khóa học" : "View Course"}
                            <ChevronRight className="size-3.5 ml-1" />
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>)}

              {/* STAGE 3 ENJOY AI (MOBILE) */}
              <div className="relative pt-2">
                <div className="absolute -left-6 top-3 size-6 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center text-[10px] font-bold">
                  03
                </div>
                <Card className="border-secondary/60 bg-secondary/10 shadow-xs">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Trophy className="size-4 text-secondary" />
                      <span className="font-bold text-sm text-foreground">
                        {lang === "vi" ? "ENJOY AI PREPARATION" : "ENJOY AI PREPARATION"}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                      {lang === "vi" ? "Lộ trình luyện thi sau HP6 (không phải HP7). Huấn luyện chiến thuật và luật đấu trường quốc tế." : "Competition track following HP6 (not HP7). Strategic mission prep for official tournaments."}
                    </p>
                    <Button asChild size="sm" variant="secondary" className="w-full text-xs">
                      <Link to="#enjoy-ai-pathway">
                        {lang === "vi" ? "Tìm hiểu lộ trình thi đấu" : "Explore Competition Track"}
                        <ChevronRight className="size-3.5 ml-1" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>

        {/* BOTTOM QUICK DECISION / TRUST HIGHLIGHT */}
        <div className="mt-16 rounded-2xl border border-border bg-muted/40 p-6 md:p-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <div className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <CheckCircle2 className="size-5" />
              </div>
              <div>
                <h5 className="text-sm font-bold text-foreground">
                  {lang === "vi" ? "Đánh giá chuẩn đầu vào" : "Accurate Entry Assessment"}
                </h5>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  {lang === "vi" ? "Học sinh 7+ tuổi có thể tham gia bài test đánh giá tư duy để bắt đầu ngay tại HP1 mà không bắt buộc học qua Duplo." : "Students aged 7+ can take a placement assessment to start directly at HP1 without prior Duplo requirement."}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="size-8 rounded-lg bg-chart-3/20 text-chart-3 flex items-center justify-center shrink-0">
                <Layers className="size-5" />
              </div>
              <div>
                <h5 className="text-sm font-bold text-foreground">
                  {lang === "vi" ? "Chuẩn bộ kit WhalesBot AI" : "Genuine WhalesBot AI Kits"}
                </h5>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  {lang === "vi" ? "100% giáo trình từ HP1 đến HP6 đồng bộ thiết bị AI Module 1S, đảm bảo tiến trình liền mạch và thực chất." : "Consistent hardware standard across all HP1-HP6 stages with certified AI Module 1S platforms."}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="size-8 rounded-lg bg-secondary/20 text-secondary flex items-center justify-center shrink-0">
                <GraduationCap className="size-5" />
              </div>
              <div>
                <h5 className="text-sm font-bold text-foreground">
                  {lang === "vi" ? "Tư vấn lộ trình cá nhân hóa" : "Personalized Education Guidance"}
                </h5>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  {lang === "vi" ? "Đội ngũ chuyên môn sẵn sàng tư vấn trực tiếp theo độ tuổi và năng lực từng trẻ." : "Academic advisors are available to recommend the optimal starting course for your child."}
                </p>
                <div className="mt-3">
                  <Button asChild size="sm" variant="default" className="text-xs h-7">
                    <Link to="/FreeTrialContactPage">
                      {lang === "vi" ? "Đăng ký tư vấn miễn phí" : "Book Advisory Session"}
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}